import React from 'react';
import styled from 'styled-components';
import type { AccountState } from './accountState';

const Card = styled.div<{ $dropdown: boolean; $bare?: boolean }>`
  /* $bare: the host popover already draws the cream surface, border, radius and
     shadow (the board account menu), so the card drops its own chrome to avoid a
     card-in-a-card double outline. The inner content (title / copy / CTA / links)
     is unchanged; only the outer shell is suppressed. */
  background: ${({ theme, $bare }) => ($bare ? 'transparent' : theme.color.creamLight)};
  border: ${({ theme, $bare }) => ($bare ? '0' : `1px solid ${theme.color.warmBorder}`)};
  border-radius: ${({ $dropdown, $bare }) => ($bare ? '0' : $dropdown ? '14px' : '18px')};
  padding: ${({ $dropdown, $bare }) =>
    $bare ? '0' : $dropdown ? '14px 14px 15px' : '20px 18px 22px'};
  font-family: ${({ theme }) => theme.font.sans};
`;

const Title = styled.div<{ $dropdown: boolean }>`
  /* The unconfirmed headline carries a full email address — never let it
     overflow the card on a 375px phone. */
  overflow-wrap: anywhere;
  font-size: ${({ $dropdown }) => ($dropdown ? '14.5px' : '17px')};
  font-weight: 600;
  color: hsl(var(--foreground));
`;

const Copy = styled.p<{ $dropdown: boolean }>`
  margin: 6px 0 0;
  font-size: ${({ $dropdown }) => ($dropdown ? '13px' : '14.5px')};
  line-height: 1.45;
  color: hsl(var(--muted-foreground));
  overflow-wrap: anywhere;
`;

const PrimaryButton = styled.button`
  margin-top: 14px;
  width: 100%;
  border: none;
  border-radius: 24px;
  padding: 12px 16px;
  font-family: inherit;
  font-size: 15px;
  font-weight: 500;
  color: #ffffff;
  background: hsl(var(--primary));
  cursor: pointer;
  &:hover {
    background: ${({ theme }) => theme.color.clayPressed};
  }
`;

export interface V4YouAccountCardProps {
  state: AccountState;
  layout?: 'page' | 'dropdown';
  bare?: boolean;
  onSignIn: () => void;
  // Legacy gallery props; the account card now has a single auth action.
  onOpenCapture?: () => void;
  onResend?: () => void;
  onFixEmail?: (email: string) => void;
  resendEnabled?: boolean;
}

export const V4YouAccountCard: React.FC<V4YouAccountCardProps> = ({
  state, layout = 'page', bare = false, onSignIn,
}) => {
  if (state.signedIn) return null;
  const dropdown = layout === 'dropdown';
  return <Card $dropdown={dropdown} $bare={bare} data-testid="v4-you-save-boards">
    <Title $dropdown={dropdown}>{state.boards.length ? 'Keep your boards' : 'Your Thea account'}</Title>
    <Copy $dropdown={dropdown}>Create an account to save your boards, or log in to see your account.</Copy>
    <PrimaryButton type="button" data-signin-entry="v4-you-card" onClick={onSignIn}>
      Log in or create account
    </PrimaryButton>
  </Card>;
};

export default V4YouAccountCard;
